import { t } from "@lingui/macro";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { PrivateTimeSlotCard } from "@whoosh/design-system/src/apps/PatronApp/screens/teesheet/components/TeeSheet/TimeSlotCard/";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { PlayerGolfIcons } from "./PlayerGolfIcons";
import { AgendaWaitlistBooking_Booking$key } from "./__generated__/AgendaWaitlistBooking_Booking.graphql";

// Placeholder for now until we get a more finalized way to get this value
// Max player count is not really a thing for waitlists and queueSlots so we'll just use 4 for now
const DEFAULT_MAX_PLAYER_COUNT = 4;

type AgendaWaitlistBookingProps = {
  queryRef: AgendaWaitlistBooking_Booking$key;
  onWaitlistBookingPress: (bookingId: string) => void;
};

export const AgendaWaitlistBooking = ({
  queryRef,
  onWaitlistBookingPress,
}: AgendaWaitlistBookingProps) => {
  const currentProfile = useAtomValue(currentProfileAtom);
  const data = useFragment(AgendaWaitlistBookingFragmentString, queryRef);
  let formattedTime = data.request?.time
    ? format(
        parse(data.request?.time || "00:00:00", "HH:mm:ss", new Date()),
        "h:mm aaa"
      )
    : undefined;

  const { onPress: newPatronEditBookingLinkProps } = useLinkProps({
    screen: "BookingSummary",
    params: {
      slotOrBookingId: data.id,
    },
  });

  const isDisabled = data.writeAccess === "NONE" && !data.canUpdate;
  const canPress =
    !!data.canUpdate || ["JOIN", "FULL_WRITE"].includes(data.writeAccess ?? "");
  const players =
    data.rounds?.edges?.map((r) => {
      const player = r?.node?.player;
      const isGuest = r?.node?.__typename === "GuestRound";
      const fullName = `${player?.firstName || "Unknown"} ${
        player?.lastName ? player?.lastName : isGuest ? "Guest" : "Member"
      }`;
      const holeCount = r?.node?.courseLayout?.holeCount || "EIGHTEEN";
      const transportationType = r?.node?.transportationType?.code || "WALK";
      return {
        fullName,
        icons: (
          <PlayerGolfIcons
            holeCount={holeCount}
            transportationType={transportationType}
          />
        ),
        isGuest: isGuest,
      };
    }) || [];

  const isCurrentUserInBooking = data.rounds?.edges?.some(
    (e) => e?.node?.player?.id === currentProfile?.memberId
  );

  const ctaText =
    canPress && !isDisabled
      ? isCurrentUserInBooking
        ? t`Manage Booking`
        : t`Join This Group`
      : undefined;

  return (
    <PrivateTimeSlotCard
      borderRadius={{
        top: "4",
        bottom: "4",
      }}
      isUnavailable={!canPress || isDisabled}
      time={formattedTime}
      players={players}
      onPress={
        canPress && !isDisabled
          ? () =>
              isCurrentUserInBooking
                ? newPatronEditBookingLinkProps()
                : onWaitlistBookingPress(data.id ?? "")
          : undefined
      }
      availableSpots={DEFAULT_MAX_PLAYER_COUNT - players.length}
      ctaText={ctaText}
      isQueueSlot
      canUpdate={canPress}
      currentUserIsInBooking={isCurrentUserInBooking}
    />
  );
};

const AgendaWaitlistBookingFragmentString = graphql`
  fragment AgendaWaitlistBooking_Booking on Booking {
    id
    canUpdate
    writeAccess
    ... on StandardBooking {
      request {
        time
        id
      }
    }
    startSlot {
      id
      ... on QueueSlot {
        id
        agenda {
          date
        }
      }
    }
    rounds(first: 100) @connection(key: "booking_rounds") {
      edges {
        node {
          id
          __typename
          transportationType {
            id
            code
          }
          courseLayout {
            id
            holeCount
          }
          player {
            __typename
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;
